import {calculateErrorMessage} from 'common/helpers'
import {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'
import {Button} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

import Spinner from '../../../../../common/components/Spinner'
import {
  cleanupAllWalkthroughAttributes,
  getElementBounds,
  isElementInViewport,
  resolveWalkthroughTargetSync,
} from '../utils/walkthroughDom'
import {
  getCurrentStep,
  isFirstStep,
  isLastStep,
  useDynamicWalkthrough,
} from './DynamicWalkthroughContext'

let globalOverlayInstanceId: string | null = null

interface TooltipPosition {
  top: number
  left: number
  arrowPosition: 'top' | 'bottom' | 'left' | 'right'
}

const WALKTHROUGH_THEME = {
  primary: '#0d6efd',
  primaryDark: '#0b5ed7',
  text: '#0f172a',
  muted: '#4b5563',
  surface: '#ffffff',
  surfaceMuted: '#f5f7fb',
  danger: '#ef4444',
  dangerSurface: '#fee2e2',
  dangerBorder: '#fecdd3',
  shadow: '0 18px 48px rgba(15,23,42,0.18)',
  radius: 16,
}

export function DynamicWalkthroughOverlay({theme = 'light'}: {theme?: 'light' | 'dark'}) {
  const intl = useIntl()
  const {state, actions} = useDynamicWalkthrough()
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null)
  const [primaryHighlightBounds, setPrimaryHighlightBounds] = useState<DOMRect | null>(null)
  const [highlightBoundsList, setHighlightBoundsList] = useState<DOMRect[]>([])
  const [isReadyToRender, setIsReadyToRender] = useState<boolean>(false)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const backdropRef = useRef<HTMLDivElement | null>(null)
  const positionUpdateTimer = useRef<NodeJS.Timeout | null>(null)
  const instanceId = useRef(`overlay-${Date.now()}-${Math.random()}`).current
  const [isActiveInstance, setIsActiveInstance] = useState(false)
  const resetAppliedRef = useRef(false)
  const [wasAborted, setWasAborted] = useState(false)
  const totalSteps = state.steps?.length || 0
  const safeProgress = totalSteps
    ? Math.min(100, Math.max(10, Math.round(((state.currentStepIndex + 1) / totalSteps) * 100)))
    : 0
  const firstStep = isFirstStep(state)
  const lastStep = isLastStep(state)

  const currentStep = getCurrentStep(state)

  useEffect(() => {
    if (!state.isActive || !state.currentElement || !currentStep) return

    let observer: ResizeObserver | null = null
    const targets = currentStep.walkthrough.target.split(',').map((s) => s.trim())

    const updateHighlights = () => {
      const allBounds: DOMRect[] = []

      targets.forEach((selector) => {
        const el = resolveWalkthroughTargetSync(selector)
        if (el) {
          allBounds.push(getElementBounds(el))
        }
      })

      if (allBounds.length > 0) {
        setHighlightBoundsList(allBounds)
        setPrimaryHighlightBounds(allBounds[0])
      }
    }

    observer = new ResizeObserver(() => {
      updateHighlights()
    })

    const elementsToObserve = targets
      .map((s) => resolveWalkthroughTargetSync(s))
      .filter((el): el is HTMLElement => !!el)

    elementsToObserve.forEach((el) => observer?.observe(el))
    updateHighlights()

    // ResizeObserver only fires when an element's own size changes; a window
    // resize or scroll can move a target without resizing it, which would
    // leave the highlight stranded at stale coordinates.
    window.addEventListener('resize', updateHighlights)
    window.addEventListener('scroll', updateHighlights, true)

    return () => {
      window.removeEventListener('resize', updateHighlights)
      window.removeEventListener('scroll', updateHighlights, true)
      if (observer) {
        elementsToObserve.forEach((el) => observer?.unobserve(el))
        observer.disconnect()
      }
    }
  }, [state.isActive, state.currentElement, currentStep])

  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent
      if (customEvent.detail && customEvent.detail.completed === false) {
        setWasAborted(true)
      }
    }

    window.addEventListener('dynamic-walkthrough-end', handler)
    return () => window.removeEventListener('dynamic-walkthrough-end', handler)
  }, [])

  useEffect(() => {
    if (state.isActive) {
      if (!globalOverlayInstanceId || globalOverlayInstanceId === instanceId) {
        globalOverlayInstanceId = instanceId
        setIsActiveInstance(true)
      } else {
        setIsActiveInstance(false)
      }
    } else {
      if (globalOverlayInstanceId === instanceId) {
        globalOverlayInstanceId = null
      }
      setIsActiveInstance(false)
    }

    return () => {
      if (globalOverlayInstanceId === instanceId) {
        globalOverlayInstanceId = null
      }

      try {
        cleanupAllWalkthroughAttributes()
        const portal = document.getElementById('walkthrough-overlay-portal-root')
        if (portal) portal.remove()

        document
          .querySelectorAll(
            '.walkthrough-backdrop, .walkthrough-highlight, .walkthrough-tooltip, .walkthrough-loading-overlay, .walkthrough-transition-loader'
          )
          .forEach((el) => el.remove())
      } catch (err) {
        calculateErrorMessage(err)
      }
    }
  }, [state.isActive, instanceId])

  useEffect(() => {
    const handler = () => {
      try {
        cleanupAllWalkthroughAttributes()
        if (globalOverlayInstanceId === instanceId) globalOverlayInstanceId = null
        const portal = document.getElementById('walkthrough-overlay-portal-root')
        if (portal) portal.remove()

        document
          .querySelectorAll(
            '.walkthrough-backdrop, .walkthrough-highlight, .walkthrough-tooltip, .walkthrough-loading-overlay, .walkthrough-transition-loader'
          )
          .forEach((el) => el.remove())
      } catch (err) {
        calculateErrorMessage(err)
      }
    }

    window.addEventListener('dynamic-walkthrough-force-cleanup', handler)
    return () => window.removeEventListener('dynamic-walkthrough-force-cleanup', handler)
  }, [instanceId])

  const updatePositions = useCallback(() => {
    if (state.isNavigating) {
      if (positionUpdateTimer.current) {
        clearTimeout(positionUpdateTimer.current)
      }
      return
    }

    if (positionUpdateTimer.current) {
      clearTimeout(positionUpdateTimer.current)
    }

    positionUpdateTimer.current = setTimeout(() => {
      if (!state.isActive || !currentStep) {
        setTooltipPosition(null)
        setPrimaryHighlightBounds(null)
        return
      }

      if (!state.currentElement || state.isNavigating) {
        return
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!state.currentElement) return

          if (!isElementInViewport(state.currentElement)) {
            state.currentElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            })
          }

          const bounds = getElementBounds(state.currentElement)
          setPrimaryHighlightBounds(bounds)
          setHighlightBoundsList([bounds])

          const position = calculateTooltipPosition(
            bounds,
            currentStep.walkthrough.position,
            tooltipRef.current
          )

          setTooltipPosition(position)
        })
      })
    }, 150)
  }, [state.isActive, state.currentElement, state.isNavigating, currentStep])

  useLayoutEffect(() => {
    if (state.isNavigating) {
      if (positionUpdateTimer.current) {
        clearTimeout(positionUpdateTimer.current)
      }
      return
    }

    updatePositions()

    if (state.currentElement) {
      const handleScroll = () => updatePositions()
      const handleResize = () => updatePositions()

      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleResize)

        if (positionUpdateTimer.current) {
          clearTimeout(positionUpdateTimer.current)
        }
      }
    }

    return () => {
      if (positionUpdateTimer.current) {
        clearTimeout(positionUpdateTimer.current)
      }
    }
  }, [state.currentElement, state.currentStepIndex, state.isNavigating, updatePositions])

  useLayoutEffect(() => {
    if (!isActiveInstance || !state.isActive) {
      setIsReadyToRender(false)
      return
    }

    const el = state.currentElement as HTMLElement | null
    if (!el) {
      if (!state.isActive) {
        setIsReadyToRender(false)
      }
      return
    }

    let cancelled = false

    const ensureVisible = async () => {
      try {
        const rect = el.getBoundingClientRect()
        const fullyVisible =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth

        if (!fullyVisible) {
          // Use the same approach as common/walkthrough/WalkthroughOverlay:
          // scrollIntoView with block:center works correctly when body is
          // overflow:hidden (not position:fixed), letting the browser find
          // the right scroll container automatically.
          el.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'})
          await new Promise<void>((resolve) => setTimeout(resolve, 700))
        }

        if (cancelled) return

        setIsReadyToRender(true)
      } catch (err) {
        setIsReadyToRender(true)
        calculateErrorMessage(err)
      }
    }

    setIsReadyToRender(false)
    ensureVisible()

    return () => {
      cancelled = true
      setIsReadyToRender(false)
    }
  }, [state.currentElement, state.currentStepIndex, isActiveInstance, state.isActive])

  useEffect(() => {
    if (!isActiveInstance || !state.isActive || state.isWaitingForModal) {
      return
    }

    const body = document.body
    if (!body) return

    const owner = (window as any).__walkthrough_scroll_owner
    if (owner && owner !== instanceId) return
    ;(window as any).__walkthrough_scroll_owner = instanceId

    const preventScroll = (event: Event) => {
      if (!isActiveInstance || !state.isActive) return

      if (event instanceof KeyboardEvent) {
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Space']
        if (!scrollKeys.includes(event.code)) return
      }

      event.preventDefault()
    }

    const originalOverflow = body.style.overflow
    body.style.overflow = 'hidden'

    window.addEventListener('wheel', preventScroll, {passive: false, capture: true})
    window.addEventListener('touchmove', preventScroll, {passive: false, capture: true})
    window.addEventListener('keydown', preventScroll, {capture: true})

    return () => {
      // These listeners are this instance's own closures, so they must come off
      // unconditionally — bailing out early here (as this used to, on the owner
      // check below) left capture-phase preventDefault handlers attached to the
      // window forever once another instance took ownership, which reads to the
      // user as "the page is frozen".
      window.removeEventListener('wheel', preventScroll, true)
      window.removeEventListener('touchmove', preventScroll, true)
      window.removeEventListener('keydown', preventScroll, true)

      if ((window as any).__walkthrough_scroll_owner !== instanceId) return

      body.style.overflow = originalOverflow

      delete (window as any).__walkthrough_scroll_owner
    }
  }, [isActiveInstance, state.isActive, state.isWaitingForModal, instanceId])

  // A backdrop with no tooltip on top of it is decorative — it must never
  // swallow clicks, because there is no Skip button on screen to escape with.
  // `setPortalPassThrough(false)` / `ensurePortalInteractive()` write
  // pointer-events straight onto this node, and React will not undo that (its
  // own declared value never changes), so re-assert it on every commit.
  useLayoutEffect(() => {
    const backdrop = backdropRef.current
    if (backdrop) backdrop.style.pointerEvents = 'none'
  })

  useEffect(() => {
    try {
      if (!isActiveInstance || !state.isActive) return
      if (state.currentStepIndex !== 0 || resetAppliedRef.current) return

      const targetEl = state.currentElement as HTMLElement | null

      if (!targetEl) {
        return
      }

      let container: Element | null = targetEl.closest('.accordion')
      let itemSelector = '.accordion-item'

      if (!container) {
        container = targetEl.closest('.timetable, .timetable-container')
        itemSelector = '.timetable-column, .accordion-item'
      }

      if (!container) {
        return
      }

      const items = Array.from(container.querySelectorAll(itemSelector)) as HTMLElement[]

      items.forEach((item) => {
        const collapse = item.querySelector('.accordion-collapse') as HTMLElement | null
        const btn = item.querySelector('.accordion-button') as HTMLElement | null

        if (collapse) collapse.classList.remove('show')

        if (btn) {
          btn.classList.add('collapsed')
          btn.setAttribute('aria-expanded', 'false')
        }

        const content = item.querySelector(
          '.timetable-content, [data-content], .collapse'
        ) as HTMLElement | null
        if (content) content.style.display = 'none'

        item.classList.remove('open', 'show', 'active', 'expanded')
        item.setAttribute('aria-expanded', 'false')
      })

      const first = items[0]

      if (first) {
        const collapse = first.querySelector('.accordion-collapse') as HTMLElement | null
        const btn = first.querySelector('.accordion-button') as HTMLElement | null

        if (collapse) collapse.classList.add('show')

        if (btn) {
          btn.classList.remove('collapsed')
          btn.setAttribute('aria-expanded', 'true')
        }

        const content = first.querySelector(
          '.timetable-content, [data-content], .collapse'
        ) as HTMLElement | null
        if (content) content.style.display = ''

        first.classList.add('open', 'show')
        first.setAttribute('aria-expanded', 'true')
      }

      ;['timetableOpenColumns', 'timetableState'].forEach((k) => {
        if (localStorage.getItem(k) || sessionStorage.getItem(k)) {
          localStorage.removeItem(k)
          sessionStorage.removeItem(k)
        }
      })

      resetAppliedRef.current = true
    } catch (err) {
      calculateErrorMessage(err)
    }
  }, [state.isActive, state.currentStepIndex, state.currentElement, isActiveInstance])

  useEffect(() => {
    if (!isActiveInstance || !state.isActive) return
    if (state.currentStepIndex !== 0 || resetAppliedRef.current) return

    let columns: HTMLElement[] = []
    let usedDOM = false
    let style: HTMLStyleElement | null = null

    const row = document.querySelector('.row')

    if (row) {
      columns = Array.from(
        row.querySelectorAll(':scope > .card, :scope > div.card')
      ) as HTMLElement[]
    }

    if (columns.length < 2) {
      columns = Array.from(document.querySelectorAll('.card'))
    }

    if (columns.length > 1) {
      const visibleBefore = columns.filter((c) => {
        try {
          return window.getComputedStyle(c).display !== 'none'
        } catch (e) {
          calculateErrorMessage(e)
          return false
        }
      })

      visibleBefore.forEach((c) => {
        const idx = columns.indexOf(c)
        if (idx > 0) markUserOpen(c)
      })

      let activeIdx = columns.findIndex((col) => col.contains(state.currentElement as Node))
      if (activeIdx === -1) activeIdx = 0

      columns.forEach((col, idx) => {
        if (isUserOpen(col)) {
          col.style.display = ''
          col.classList.add('walkthrough-active-column')
        } else {
          col.style.display = idx <= activeIdx ? '' : 'none'
          if (idx <= activeIdx) col.classList.add('walkthrough-active-column')
          else col.classList.remove('walkthrough-active-column')
        }
      })

      usedDOM = true
    } else {
      style = document.getElementById('walkthrough-reset-style') as HTMLStyleElement | null

      if (!style) {
        style = document.createElement('style')
        style.id = 'walkthrough-reset-style'

        style.innerHTML = `
          .row > .card:not(:first-child),
          .row > div.card:not(:first-child),
          .main-content > .card:not(:first-child) {
            display: none !important;
          }
        `
        document.head.appendChild(style)
      }

      resetAppliedRef.current = true
    }

    return () => {
      if (usedDOM) {
        columns.forEach((col) => {
          col.style.display = ''
          col.classList.remove('walkthrough-active-column')
        })
      }

      if (style && style.parentNode) {
        style.parentNode.removeChild(style)
      }

      resetAppliedRef.current = false
    }
  }, [state.isActive, state.currentStepIndex, isActiveInstance, state.currentElement])

  useEffect(() => {
    if (!isActiveInstance || !state.isActive) return
    if (!state.currentElement) return

    let style: HTMLStyleElement | null = document.getElementById(
      'walkthrough-deselect-style'
    ) as HTMLStyleElement | null

    if (!style) {
      style = document.createElement('style')
      style.id = 'walkthrough-deselect-style'

      style.innerHTML = `
        /* Visually suppress .active, .selected, .show, .open except for walkthrough-highlight */
        .card.active:not(.walkthrough-active-column):not(.walkthrough-highlight),
        .card.selected:not(.walkthrough-active-column):not(.walkthrough-highlight),
        .card.show:not(.walkthrough-active-column):not(.walkthrough-highlight),
        .card.open:not(.walkthrough-active-column):not(.walkthrough-highlight),
        .accordion-item.active:not(.walkthrough-highlight),
        .accordion-item.selected:not(.walkthrough-highlight),
        .accordion-item.show:not(.walkthrough-highlight),
        .accordion-item.open:not(.walkthrough-highlight),
        .timetable-column.active:not(.walkthrough-highlight),
        .timetable-column.selected:not(.walkthrough-highlight),
        .timetable-column.show:not(.walkthrough-highlight),
        .timetable-column.open:not(.walkthrough-highlight) {
          background: none !important;
          box-shadow: none !important;
          border-color: #eee !important;
          color: inherit !important;
          filter: grayscale(0.2) brightness(1.05);
          opacity: 0.85;
        }
      `
      document.head.appendChild(style)
    }

    let pulseStyle = document.getElementById('walkthrough-pulse-style') as HTMLStyleElement | null
    if (!pulseStyle) {
      pulseStyle = document.createElement('style')
      pulseStyle.id = 'walkthrough-pulse-style'
      pulseStyle.innerHTML = `
        @keyframes walkthrough-pulse {
          0% { box-shadow: 0 0 0 0px rgba(13, 110, 253, 0.5), 0 0 0 2px #fff; }
          70% { box-shadow: 0 0 0 15px rgba(13, 110, 253, 0), 0 0 0 2px #fff; }
          100% { box-shadow: 0 0 0 0px rgba(13, 110, 253, 0), 0 0 0 2px #fff; }
        }
        .walkthrough-highlight-premium {
          animation: walkthrough-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          border: 2px solid #fff;
          transition: all 0.3s ease;
        }
      `
      document.head.appendChild(pulseStyle)
    }

    const selectors = [
      '.card.active',
      '.card.selected',
      '.card.show',
      '.card.open',
      '.accordion-item.active',
      '.accordion-item.selected',
      '.accordion-item.show',
      '.accordion-item.open',
      '.timetable-column.active',
      '.timetable-column.selected',
      '.timetable-column.show',
      '.timetable-column.open',
    ]

    const allSelected = Array.from(document.querySelectorAll(selectors.join(','))) as HTMLElement[]

    allSelected.forEach((el) => {
      if (
        state.currentElement &&
        (el === state.currentElement || el.contains(state.currentElement))
      )
        return

      el.classList.remove('active', 'selected', 'show', 'open')
      el.setAttribute('aria-expanded', 'false')
    })

    return () => {
      if (style && style.parentNode) {
        style.parentNode.removeChild(style)
      }
      if (pulseStyle && pulseStyle.parentNode) {
        pulseStyle.parentNode.removeChild(pulseStyle)
      }
    }
  }, [state.isActive, state.currentElement, state.currentStepIndex, isActiveInstance])

  if (wasAborted) {
    return createPortal(
      <div className='walkthrough-aborted-overlay'>
        <div className='walkthrough-aborted-message'>
          <h3>
            {intl.formatMessage({
              id: 'WALKTHROUGH_PROVIDER.INFO.ABORTED',
            })}
          </h3>
          <details>
            <summary>
              {intl.formatMessage({
                id: 'WALKTHROUGH.STEPS_LIST',
              })}
            </summary>
            <ol>
              {state.steps.map((step) => (
                <li key={step.id}>
                  <strong>{step.walkthrough.title}</strong>
                  <p>{step.walkthrough.content}</p>
                </li>
              ))}
            </ol>
          </details>
        </div>
      </div>,
      document.body
    )
  }

  if (!state.isActive || !currentStep) {
    return null
  }

  if (state.isWaitingForModal) {
    return null
  }

  if (!isActiveInstance || globalOverlayInstanceId !== instanceId) {
    return null
  }

  const shouldRenderContent =
    state.currentElement && primaryHighlightBounds && tooltipPosition && isReadyToRender

  // Element-not-found: show a friendly modal then end the walkthrough
  const isElementNotFound =
    state.isActive && state.error && state.error.startsWith('Could not find element')

  if (isElementNotFound) {
    const notFoundPortal = document.createElement('div')
    notFoundPortal.style.cssText =
      'position:fixed;inset:0;z-index:1000000;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(15,23,42,0.55);pointer-events:auto;'
    document.body.appendChild(notFoundPortal)

    return createPortal(
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: '32px 28px',
          maxWidth: 400,
          width: '90vw',
          textAlign: 'center',
          boxShadow: '0 18px 48px rgba(15,23,42,0.22)',
          pointerEvents: 'auto',
        }}
      >
        <i className='fas fa-exclamation-triangle fs-2x text-warning mb-4 d-block' />
        <h5 style={{fontWeight: 700, marginBottom: 8}}>
          {intl.formatMessage({id: 'WALKTHROUGH.UNAVAILABLE.TITLE'})}
        </h5>
        <p style={{color: '#4b5563', fontSize: '0.9rem', marginBottom: 24}}>
          {intl.formatMessage({id: 'WALKTHROUGH.UNAVAILABLE.MESSAGE'})}
        </p>
        <button
          className='btn btn-primary px-6'
          onClick={() => {
            notFoundPortal.remove()
            actions.endWalkthrough(true)
          }}
        >
          {intl.formatMessage({id: 'WALKTHROUGH.BUTTON.OK'})}
        </button>
      </div>,
      notFoundPortal
    )
  }

  const getPortalRoot = () => {
    const portalId = 'walkthrough-overlay-portal-root'
    let portalRoot = document.getElementById(portalId)

    if (!portalRoot) {
      portalRoot = document.createElement('div')
      portalRoot.id = portalId
      portalRoot.style.position = 'fixed'
      portalRoot.style.top = '0'
      portalRoot.style.left = '0'
      portalRoot.style.width = '100%'
      portalRoot.style.height = '100%'
      portalRoot.style.pointerEvents = 'none'
      document.body.appendChild(portalRoot)
    }

    portalRoot.style.zIndex = '999999'
    return portalRoot
  }

  const overlayContent = (
    <div className='walkthrough-overlay-container'>
      {/* Backdrop: 4 strips leaving the highlighted element visible */}
      {shouldRenderContent && primaryHighlightBounds ? (
        (() => {
          const PAD = 10
          const b = primaryHighlightBounds
          const top = Math.max(0, b.top - PAD)
          const left = Math.max(0, b.left - PAD)
          const right = Math.min(window.innerWidth, b.right + PAD)
          const bottom = Math.min(window.innerHeight, b.bottom + PAD)
          const dim = 'rgba(15,23,42,0.55)'
          const base: React.CSSProperties = {
            position: 'fixed',
            background: dim,
            zIndex: 999990,
            pointerEvents: 'none',
          }
          return (
            <>
              {/* top strip */}
              <div style={{...base, inset: `0 0 auto 0`, height: top}} />
              {/* bottom strip */}
              <div style={{...base, inset: `auto 0 0 0`, top: bottom}} />
              {/* left strip */}
              <div
                style={{
                  ...base,
                  top,
                  bottom: `${window.innerHeight - bottom}px`,
                  left: 0,
                  right: `${window.innerWidth - left}px`,
                }}
              />
              {/* right strip */}
              <div
                style={{
                  ...base,
                  top,
                  bottom: `${window.innerHeight - bottom}px`,
                  left: right,
                  right: 0,
                }}
              />
            </>
          )
        })()
      ) : (
        <div
          ref={backdropRef}
          className='walkthrough-backdrop'
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.55)',
            zIndex: 999990,
            pointerEvents: 'none',
          }}
        />
      )}

      {shouldRenderContent &&
        highlightBoundsList.map((bounds, idx) => (
          <div
            key={`highlight-${idx}`}
            className='walkthrough-highlight walkthrough-highlight-premium'
            style={{
              position: 'absolute',
              top: `${bounds.top}px`,
              left: `${bounds.left}px`,
              width: `${bounds.width}px`,
              height: `${bounds.height}px`,
              zIndex: 999991,
              borderRadius: 12,
              background: 'transparent',
              pointerEvents: 'none',
            }}
          />
        ))}

      {shouldRenderContent && tooltipPosition && (
        <div
          ref={tooltipRef}
          className={`walkthrough-tooltip walkthrough-tooltip-${tooltipPosition.arrowPosition}`}
          style={{
            position: 'absolute',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 999999,
            background: theme === 'dark' ? '#23272f' : WALKTHROUGH_THEME.surface,
            color: theme === 'dark' ? '#fff' : WALKTHROUGH_THEME.text,
            boxShadow: WALKTHROUGH_THEME.shadow,
            borderRadius: `${WALKTHROUGH_THEME.radius}px`,
            border: 'none',
            maxWidth: 'min(400px, calc(100vw - 24px))',
            width: 'min(380px, calc(100vw - 24px))',
            maxHeight: 'calc(100vh - 24px)',
            padding: 0,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '16px 18px',
              background: WALKTHROUGH_THEME.primary,
              color: WALKTHROUGH_THEME.surface,
              borderRadius: `${WALKTHROUGH_THEME.radius}px ${WALKTHROUGH_THEME.radius}px 0 0`,
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: 4, flex: 1}}>
              <span
                style={{fontWeight: 700, fontSize: 'clamp(15px, 4.5vw, 18px)', lineHeight: 1.3}}
              >
                {currentStep.walkthrough.title}
              </span>
            </div>

            <button
              className='walkthrough-close-btn'
              onClick={() => actions.endWalkthrough(true)}
              aria-label={intl.formatMessage({id: 'WALKTHROUGH.CLOSE_ARIA'})}
              style={{
                marginLeft: 8,
                background: WALKTHROUGH_THEME.surface,
                border: 'none',
                borderRadius: '50%',
                width: 34,
                height: 34,
                boxShadow: '0 6px 18px rgba(15,23,42,0.20)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: WALKTHROUGH_THEME.text,
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              ×
            </button>
          </div>

          <div
            style={{
              background: theme === 'dark' ? '#23272f' : WALKTHROUGH_THEME.surface,
              color: theme === 'dark' ? '#fff' : WALKTHROUGH_THEME.text,
              borderRadius: `0 0 ${WALKTHROUGH_THEME.radius}px ${WALKTHROUGH_THEME.radius}px`,
              padding: 'clamp(12px, 3vw, 18px) clamp(14px, 3.5vw, 20px) 8px',
              fontSize: 'clamp(14px, 4vw, 16px)',
              fontWeight: 500,
              lineHeight: 1.6,
            }}
          >
            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14}}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: theme === 'dark' ? '#2d323b' : WALKTHROUGH_THEME.surfaceMuted,
                  color: theme === 'dark' ? '#cbd5e1' : WALKTHROUGH_THEME.muted,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '4px 12px',
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                }}
              >
                {intl.formatMessage({
                  id: `WALKTHROUGH.STEP_TYPE.${currentStep.walkthrough.stepType.toUpperCase()}`,
                })}
              </span>

              <div
                aria-label={intl.formatMessage({id: 'WALKTHROUGH.PROGRESS_ARIA'})}
                style={{
                  marginLeft: 'auto',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: theme === 'dark' ? '#2d323b' : WALKTHROUGH_THEME.surfaceMuted,
                  display: 'grid',
                  placeItems: 'center',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: theme === 'dark' ? '#23272f' : WALKTHROUGH_THEME.surface,
                    display: 'grid',
                    placeItems: 'center',
                    color: theme === 'dark' ? '#fff' : WALKTHROUGH_THEME.text,
                    fontWeight: 700,
                    fontSize: 11,
                    boxShadow: 'inset 0 0 0 2px rgba(13,110,253,0.2)',
                  }}
                >
                  {safeProgress}%
                </div>
              </div>
            </div>

            {currentStep.walkthrough.content}

            {currentStep?.walkthrough?.action !== 'none' && (
              <div
                style={{
                  color: WALKTHROUGH_THEME.danger,
                  background: WALKTHROUGH_THEME.dangerSurface,
                  borderRadius: 10,
                  padding: '10px 14px',
                  fontWeight: 600,
                  fontSize: 13,
                  marginTop: 14,
                  textAlign: 'left',
                  border: `1px solid ${WALKTHROUGH_THEME.dangerBorder}`,
                  lineHeight: 1.4,
                }}
              >
                {intl.formatMessage({id: 'WALKTHROUGH.ACTION_DISCLAIMER'})}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                marginTop: 18,
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Left: End Tour (always visible) */}
              <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                <Button
                  variant='danger'
                  onClick={() => actions.endWalkthrough(true)}
                  style={{
                    minWidth: 96,
                    fontWeight: 700,
                    background: WALKTHROUGH_THEME.danger,
                    border: `1px solid ${WALKTHROUGH_THEME.danger}`,
                    color: WALKTHROUGH_THEME.surface,
                    borderRadius: 999,
                    padding: '8px 14px',
                  }}
                >
                  {intl.formatMessage({id: 'WALKTHROUGH.BUTTON.END_TOUR'})}
                </Button>
              </div>

              <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
                {!firstStep && (
                  <Button
                    variant='light'
                    onClick={() => actions.previousStep()}
                    title={intl.formatMessage({id: 'WALKTHROUGH.BUTTON.PREVIOUS'})}
                    aria-label={intl.formatMessage({id: 'WALKTHROUGH.BUTTON.PREVIOUS'})}
                    style={{
                      minWidth: 48,
                      width: 48,
                      height: 40,
                      fontWeight: 700,
                      color: WALKTHROUGH_THEME.text,
                      background: WALKTHROUGH_THEME.surfaceMuted,
                      border: `1px solid ${WALKTHROUGH_THEME.surfaceMuted}`,
                      borderRadius: 12,
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className='fa fa-angle-left' aria-hidden='true' />
                  </Button>
                )}

                {!currentStep.walkthrough.hideNext &&
                  (lastStep ? (
                    <Button
                      variant='primary'
                      onClick={() => actions.endWalkthrough(false)}
                      style={{
                        minWidth: 96,
                        fontWeight: 700,
                        background: WALKTHROUGH_THEME.primary,
                        border: `1px solid ${WALKTHROUGH_THEME.primaryDark}`,
                        color: WALKTHROUGH_THEME.surface,
                        borderRadius: 999,
                        padding: '8px 14px',
                      }}
                    >
                      {intl.formatMessage({id: 'WALKTHROUGH.BUTTON.FINISH'})}
                    </Button>
                  ) : (
                    <Button
                      variant='primary'
                      onClick={() => actions.nextStep()}
                      style={{
                        minWidth: 96,
                        fontWeight: 700,
                        background: WALKTHROUGH_THEME.primary,
                        border: `1px solid ${WALKTHROUGH_THEME.primaryDark}`,
                        color: WALKTHROUGH_THEME.surface,
                        borderRadius: 999,
                        padding: '8px 14px',
                      }}
                    >
                      {intl.formatMessage({id: 'WALKTHROUGH.BUTTON.NEXT'})}
                    </Button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {state.isTransitioning && (
        <div className='walkthrough-loading-overlay'>
          <div className='walkthrough-transition-loader'>
            <Spinner />
            <p>
              {state.isTransitioning
                ? intl.formatMessage({id: 'WALKTHROUGH.LOADING_NEXT'})
                : intl.formatMessage({id: 'WALKTHROUGH.PREPARING'})}
            </p>
          </div>
        </div>
      )}

      {state.error && (
        <div className='walkthrough-error-toast'>
          <span>{state.error}</span>
          <button onClick={() => actions.setError(null)}>×</button>
        </div>
      )}
    </div>
  )

  return createPortal(overlayContent, getPortalRoot())
}

function calculateTooltipPosition(
  elementBounds: DOMRect,
  preferredPosition: 'top' | 'bottom' | 'left' | 'right' | 'center',
  tooltipElement: HTMLElement | null
): TooltipPosition {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const isNarrowViewport = viewportWidth < 480

  const TOOLTIP_OFFSET = isNarrowViewport ? 14 : 30
  const MINIMUM_GAP = isNarrowViewport ? 10 : 20
  const PADDING = isNarrowViewport ? 12 : 20

  let TOOLTIP_WIDTH = 360
  let TOOLTIP_HEIGHT = 400

  if (tooltipElement) {
    const tooltipRect = tooltipElement.getBoundingClientRect()
    if (tooltipRect.width > 0) TOOLTIP_WIDTH = tooltipRect.width
    if (tooltipRect.height > 0) TOOLTIP_HEIGHT = tooltipRect.height
  }

  // The rendered tooltip caps itself at calc(100vw - 24px) / calc(100vh - 24px),
  // so clamp the fallback estimates the same way — otherwise on small screens
  // the math places a phantom 360x400 box and pins it off-viewport.
  TOOLTIP_WIDTH = Math.min(TOOLTIP_WIDTH, viewportWidth - PADDING * 2)
  TOOLTIP_HEIGHT = Math.min(TOOLTIP_HEIGHT, viewportHeight - PADDING * 2)

  let top = 0
  let left = 0
  let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top'

  const wouldOverlap = (tooltipTop: number, tooltipLeft: number): boolean => {
    const tooltipRight = tooltipLeft + TOOLTIP_WIDTH
    const tooltipBottom = tooltipTop + TOOLTIP_HEIGHT

    const elementRight = elementBounds.left + elementBounds.width
    const elementBottom = elementBounds.top + elementBounds.height
    const elementLeft = elementBounds.left
    const elementTop = elementBounds.top

    const horizontalOverlap =
      tooltipLeft < elementRight + MINIMUM_GAP && tooltipRight > elementLeft - MINIMUM_GAP

    const verticalOverlap =
      tooltipTop < elementBottom + MINIMUM_GAP && tooltipBottom > elementTop - MINIMUM_GAP

    return horizontalOverlap && verticalOverlap
  }

  const positions: Array<{
    pos: 'top' | 'bottom' | 'left' | 'right' | 'center'
    arrow: 'top' | 'bottom' | 'left' | 'right'
  }> = []

  switch (preferredPosition) {
    case 'top':
      positions.push(
        {pos: 'top', arrow: 'bottom'},
        {pos: 'bottom', arrow: 'top'},
        {pos: 'right', arrow: 'left'},
        {pos: 'left', arrow: 'right'}
      )
      break
    case 'bottom':
      positions.push(
        {pos: 'bottom', arrow: 'top'},
        {pos: 'top', arrow: 'bottom'},
        {pos: 'right', arrow: 'left'},
        {pos: 'left', arrow: 'right'}
      )
      break
    case 'left':
      positions.push(
        {pos: 'left', arrow: 'right'},
        {pos: 'right', arrow: 'left'},
        {pos: 'top', arrow: 'bottom'},
        {pos: 'bottom', arrow: 'top'}
      )
      break
    case 'right':
      positions.push(
        {pos: 'right', arrow: 'left'},
        {pos: 'left', arrow: 'right'},
        {pos: 'top', arrow: 'bottom'},
        {pos: 'bottom', arrow: 'top'}
      )
      break
    case 'center':
      positions.push({pos: 'center', arrow: 'top'})
      break
  }

  for (const {pos, arrow} of positions) {
    let testTop = 0
    let testLeft = 0

    switch (pos) {
      case 'top':
        testTop = elementBounds.top - TOOLTIP_HEIGHT - TOOLTIP_OFFSET
        testLeft = elementBounds.left + elementBounds.width / 2 - TOOLTIP_WIDTH / 2
        break
      case 'bottom':
        testTop = elementBounds.bottom + TOOLTIP_OFFSET
        testLeft = elementBounds.left + elementBounds.width / 2 - TOOLTIP_WIDTH / 2
        break
      case 'left':
        testTop = elementBounds.top + elementBounds.height / 2 - TOOLTIP_HEIGHT / 2
        testLeft = elementBounds.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET
        break
      case 'right':
        testTop = elementBounds.top + elementBounds.height / 2 - TOOLTIP_HEIGHT / 2
        testLeft = elementBounds.right + TOOLTIP_OFFSET
        break
      case 'center':
        testTop = viewportHeight / 2 - TOOLTIP_HEIGHT / 2
        testLeft = viewportWidth / 2 - TOOLTIP_WIDTH / 2
        break
    }

    const clampedLeft = Math.max(
      PADDING,
      Math.min(testLeft, viewportWidth - TOOLTIP_WIDTH - PADDING)
    )
    const clampedTop = Math.max(
      PADDING,
      Math.min(testTop, viewportHeight - TOOLTIP_HEIGHT - PADDING)
    )

    if (pos === 'center' || !wouldOverlap(clampedTop, clampedLeft)) {
      top = clampedTop
      left = clampedLeft
      arrowPosition = arrow
      break
    }

    if (top === 0 && left === 0) {
      top = clampedTop
      left = clampedLeft
      arrowPosition = arrow
    }
  }

  if (top === 0 && left === 0 && arrowPosition === 'top') {
    top = Math.max(PADDING, viewportHeight / 2 - TOOLTIP_HEIGHT / 2)
    left = Math.max(PADDING, viewportWidth / 2 - TOOLTIP_WIDTH / 2)
  }

  return {top, left, arrowPosition}
}

const markUserOpen = (col: HTMLElement) => {
  try {
    col.dataset.walkthroughUserOpen = 'true'
    col.classList.add('walkthrough-user-open')
  } catch (e) {
    calculateErrorMessage(e)
  }
}

const isUserOpen = (col: HTMLElement) => {
  try {
    return (
      col.dataset?.walkthroughUserOpen === 'true' || col.classList.contains('walkthrough-user-open')
    )
  } catch (e) {
    calculateErrorMessage(e)
    return false
  }
}
